import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import CertificateView, { CertificateProgressCard } from "../components/CertificateView";
import {
  ArrowLeft, Trophy, Users, Calendar, Leaf, Target, Droplets,
  DollarSign, Scale, Plus, ChevronRight, Vote, Award, Clock,
  BookOpen, GraduationCap, Sparkles, Heart, X, Check, Edit,
  Loader2, TrendingUp, Flame, Apple, Key, Copy, Printer,
  Lock, Unlock, Calculator, Sprout, Star, Bell, BellOff
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Tropical Calypso Color Palette - VIBRANT
const calypso = {
  green: {
    primary: "#00A86B",      // Vivid jade green
    light: "#50C878",        // Bright emerald
    dark: "#006B3C",         // Deep jungle
    muted: "#D0F0C0",        // Tea green
    vibrant: "#32DE84"       // Neon spring green
  },
  mango: {
    primary: "#FF9F1C",      // Bright mango orange
    light: "#FFBF69",        // Golden mango
    dark: "#E67E22",         // Deep mango
    glow: "#FFF3CD",         // Warm cream
    vibrant: "#FFD93D"       // Electric yellow
  },
  plum: {
    primary: "#9B59B6",      // Vivid plum
    light: "#D7BDE2",        // Soft lavender
    dark: "#6C3483",         // Deep purple
    vibrant: "#A855F7"       // Electric purple
  },
  guava: {
    primary: "#FF6B6B",      // Bright coral
    light: "#FFA07A",        // Light salmon
    vibrant: "#FF4757"       // Hot coral
  },
  coconut: {
    cream: "#FFFBEB",        // Warm white
    shell: "#A0522D"         // Sienna
  },
  sky: {
    light: "#87CEEB",        // Sky blue
    vibrant: "#00D4FF"       // Cyan
  }
};

// Month icons mapping
const MONTH_ICONS = {
  january: "🥗",
  february: "🌿",
  march: "🌈",
  april: "🌸",
  may: "🌳",
  june: "🏪",
  july: "🏜️",
  august: "🌱",
  september: "🦋",
  october: "🏙️",
  november: "❄️",
  december: "🌿"
};

// 12-Month Challenge Calendar Component
const MonthlyChallengCalendar = ({ user, login }) => {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [monthDetails, setMonthDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [seedBalance, setSeedBalance] = useState(null);
  const [showMathSection, setShowMathSection] = useState({});
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [subscribingNotifications, setSubscribingNotifications] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadSchedule();
    if (user) {
      loadSeedBalance();
      checkNotificationStatus();
    }
  }, [user]);

  const loadSchedule = async () => {
    try {
      const response = await axios.get(`${API}/auto-challenges/schedule?year=2026`);
      setSchedule(response.data.schedule || []);
    } catch (error) {
      console.error("Failed to load challenge schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadSeedBalance = async () => {
    try {
      const response = await axios.get(`${API}/seeds/my-balance`, { withCredentials: true });
      setSeedBalance(response.data);
    } catch (error) {
      console.error("Failed to load seed balance:", error);
    }
  };

  const checkNotificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/notifications/challenge-unlock/status`, { withCredentials: true });
      setNotificationsEnabled(response.data.subscribed);
    } catch (error) {
      console.error("Failed to check notification status:", error);
    }
  };

  const toggleNotifications = async () => {
    if (!user) {
      toast.error("Please sign in to enable notifications");
      return;
    }
    
    setSubscribingNotifications(true);
    try {
      if (notificationsEnabled) {
        // Unsubscribe - just update preference
        await axios.post(`${API}/notifications/challenge-unlock`, { type: "none" }, { withCredentials: true });
        setNotificationsEnabled(false);
        toast.success("Notifications disabled");
      } else {
        // Subscribe to email notifications
        const response = await axios.post(`${API}/notifications/challenge-unlock`, { type: "email" }, { withCredentials: true });
        setNotificationsEnabled(true);
        toast.success(response.data.message || "Notifications enabled!");
        
        // If VIDE teacher, they get bonus seeds
        if (response.data.bonus_seeds) {
          toast.success(`🌱 +${response.data.bonus_seeds} bonus seeds!`, { duration: 4000 });
          loadSeedBalance(); // Refresh balance
        }
      }
    } catch (error) {
      toast.error("Failed to update notifications");
    } finally {
      setSubscribingNotifications(false);
    }
  };

  const loadMonthDetails = async (monthKey) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/auto-challenges/${monthKey}?year=2026`);
      setMonthDetails(response.data);
      setSelectedMonth(monthKey);
    } catch (error) {
      toast.error("Failed to load challenge details");
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleMathSection = (index) => {
    setShowMathSection(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const now = new Date();
  const currentMonth = now.getMonth() + 1; // 1-indexed

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: calypso.green.primary }} />
        <p className="text-gray-500 mt-4">Loading 12-month challenge schedule...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Seed Balance Card */}
      {user && seedBalance && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-6"
          style={{
            background: `linear-gradient(135deg, ${calypso.mango.glow}, ${calypso.green.muted})`,
            boxShadow: `0 10px 30px ${calypso.mango.primary}20`
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl"
                   style={{ background: `linear-gradient(135deg, ${calypso.mango.primary}, ${calypso.mango.dark})` }}>
                🌱
              </div>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: calypso.green.dark }}>
                  {seedBalance.balance} Seeds
                </h3>
                <p className="text-gray-600">Lifetime earned: {seedBalance.lifetime_earned}</p>
              </div>
            </div>
            <div className="text-right flex flex-col items-end gap-2">
              <Badge className="px-3 py-1 text-white rounded-full" 
                     style={{ background: calypso.green.primary }}>
                {seedBalance.certificate_level}
              </Badge>
              <p className="text-sm text-gray-500">{seedBalance.certificate_phase}</p>
              <Button
                size="sm"
                variant={notificationsEnabled ? "default" : "outline"}
                onClick={toggleNotifications}
                disabled={subscribingNotifications}
                className="mt-1"
                style={notificationsEnabled ? { background: calypso.green.primary } : {}}
                data-testid="toggle-notifications"
              >
                {subscribingNotifications ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : notificationsEnabled ? (
                  <>
                    <Bell className="w-4 h-4 mr-1" /> Notifications On
                  </>
                ) : (
                  <>
                    <BellOff className="w-4 h-4 mr-1" /> Get Notified
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Notification Banner for non-signed-in users */}
      {!user && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 rounded-xl flex items-center justify-between"
          style={{ background: `linear-gradient(135deg, ${calypso.green.muted}, ${calypso.mango.glow})` }}
        >
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6" style={{ color: calypso.green.primary }} />
            <div>
              <p className="font-medium" style={{ color: calypso.green.dark }}>Get notified when new challenges unlock!</p>
              <p className="text-sm text-gray-600">Sign in to enable notifications and track your progress</p>
            </div>
          </div>
          <Button onClick={login} style={{ background: calypso.green.primary }} className="text-white">
            Sign In
          </Button>
        </motion.div>
      )}

      {/* Month Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {schedule.map((month, index) => {
          const releaseDate = new Date(month.release_date);
          const isUnlocked = now >= releaseDate;
          const isCurrentMonth = month.month_num === currentMonth;
          const isPast = month.month_num < currentMonth;
          
          return (
            <motion.div
              key={month.month}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: isUnlocked ? 1.03 : 1 }}
              className={`relative rounded-2xl p-5 cursor-pointer transition-all ${
                isUnlocked ? 'hover:shadow-lg' : 'opacity-70'
              }`}
              style={{
                background: isCurrentMonth 
                  ? `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})`
                  : isPast && isUnlocked
                  ? `linear-gradient(135deg, ${calypso.green.muted}, white)`
                  : 'white',
                border: isCurrentMonth ? 'none' : `2px solid ${isUnlocked ? calypso.green.light : '#e5e7eb'}`,
                boxShadow: isCurrentMonth ? `0 10px 30px ${calypso.green.primary}40` : '0 2px 10px rgba(0,0,0,0.05)'
              }}
              onClick={() => loadMonthDetails(month.month)}
              data-testid={`month-card-${month.month}`}
            >
              {/* Lock/Unlock indicator */}
              <div className="absolute top-3 right-3">
                {isUnlocked ? (
                  isPast ? (
                    <Check className="w-5 h-5" style={{ color: calypso.green.primary }} />
                  ) : (
                    <Unlock className="w-5 h-5" style={{ color: isCurrentMonth ? 'white' : calypso.green.primary }} />
                  )
                ) : (
                  <Lock className="w-5 h-5 text-gray-400" />
                )}
              </div>

              {/* Month emoji */}
              <div className="text-4xl mb-3">{MONTH_ICONS[month.month]}</div>

              {/* Month name */}
              <h3 className={`font-bold text-lg ${isCurrentMonth ? 'text-white' : ''}`}
                  style={{ color: isCurrentMonth ? 'white' : calypso.green.dark }}>
                {month.month.charAt(0).toUpperCase() + month.month.slice(1)}
              </h3>

              {/* Challenge title */}
              <p className={`text-sm mt-1 line-clamp-2 ${isCurrentMonth ? 'text-white/80' : 'text-gray-600'}`}>
                {month.title.replace(/^[^\s]+\s/, '')}
              </p>

              {/* Release date */}
              <p className={`text-xs mt-2 ${isCurrentMonth ? 'text-white/60' : 'text-gray-400'}`}>
                {isUnlocked ? '✓ Unlocked' : `Unlocks ${releaseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
              </p>

              {/* Flagship/Math anchor badges */}
              {(month.is_flagship || month.is_math_anchor) && (
                <div className="flex gap-1 mt-2">
                  {month.is_flagship && (
                    <Badge className="text-xs px-2 py-0.5" style={{ background: calypso.mango.primary, color: 'white' }}>
                      ⭐ Flagship
                    </Badge>
                  )}
                  {month.is_math_anchor && (
                    <Badge className="text-xs px-2 py-0.5" style={{ background: calypso.plum.primary, color: 'white' }}>
                      📐 Math
                    </Badge>
                  )}
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Challenge Detail Modal */}
      <AnimatePresence>
        {selectedMonth && monthDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedMonth(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8"
              onClick={(e) => e.stopPropagation()}
              style={{ boxShadow: `0 30px 60px ${calypso.green.primary}25` }}
            >
              {/* Header */}
              <div className="p-6 rounded-t-3xl text-white relative overflow-hidden"
                   style={{ background: `linear-gradient(135deg, ${calypso.green.dark}, ${calypso.green.primary}, ${calypso.mango.dark})` }}>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMonth(null)}
                        className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full w-10 h-10 p-0">
                  <X className="w-5 h-5" />
                </Button>
                
                <div className="text-5xl mb-3">{MONTH_ICONS[selectedMonth]}</div>
                <h2 className="text-2xl font-bold">{monthDetails.title}</h2>
                {monthDetails.subtitle && (
                  <p className="text-white/80 mt-1">{monthDetails.subtitle}</p>
                )}
                
                <div className="flex items-center gap-4 mt-4 text-sm">
                  <Badge className="bg-white/20 text-white px-3 py-1 rounded-full">
                    {monthDetails.status === 'locked' ? '🔒 Locked' : '🔓 Unlocked'}
                  </Badge>
                  {monthDetails.theme && (
                    <Badge className="bg-white/20 text-white px-3 py-1 rounded-full">
                      {monthDetails.theme.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-6">
                {monthDetails.status === 'locked' ? (
                  /* Locked state - show teaser only */
                  <div className="text-center py-8">
                    <Lock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-bold text-gray-700 mb-2">Challenge Locked</h3>
                    <p className="text-gray-500 mb-4">{monthDetails.teaser}</p>
                    <p className="text-sm text-gray-400">
                      Unlocks on {new Date(monthDetails.release_date).toLocaleDateString('en-US', { 
                        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
                      })} at 9:00 AM
                    </p>
                    {monthDetails.hint && (
                      <p className="mt-4 text-sm" style={{ color: calypso.mango.dark }}>
                        💡 Hint: {monthDetails.hint}
                      </p>
                    )}
                  </div>
                ) : (
                  /* Unlocked state - show full details */
                  <>
                    {/* Description */}
                    <div>
                      <h3 className="font-bold text-lg mb-2" style={{ color: calypso.green.dark }}>
                        <Sparkles className="w-5 h-5 inline mr-2" style={{ color: calypso.mango.primary }} />
                        Challenge Description
                      </h3>
                      <p className="text-gray-600">{monthDetails.description}</p>
                    </div>

                    {/* Featured Plants */}
                    {monthDetails.featured_plants?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-lg mb-2" style={{ color: calypso.green.dark }}>
                          <Leaf className="w-5 h-5 inline mr-2" style={{ color: calypso.green.primary }} />
                          Featured Plants
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {monthDetails.featured_plants.map((plant, i) => (
                            <Badge key={i} variant="outline" className="px-3 py-1 rounded-full"
                                   style={{ borderColor: calypso.green.light, color: calypso.green.dark }}>
                              🌱 {plant}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tasks */}
                    {monthDetails.tasks?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-lg mb-2" style={{ color: calypso.green.dark }}>
                          <Target className="w-5 h-5 inline mr-2" style={{ color: calypso.mango.primary }} />
                          Challenge Tasks
                        </h3>
                        <ul className="space-y-2">
                          {monthDetails.tasks.map((task, i) => (
                            <li key={i} className="flex items-start gap-2 text-gray-600">
                              <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: calypso.green.primary }} />
                              {task}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Digging Deeper: Math Section */}
                    {monthDetails.digging_deeper?.length > 0 && (
                      <div className="rounded-xl overflow-hidden" style={{ border: `2px solid ${calypso.plum.light}` }}>
                        <button
                          onClick={() => toggleMathSection('main')}
                          className="w-full p-4 flex items-center justify-between text-left"
                          style={{ background: `linear-gradient(135deg, ${calypso.plum.light}30, white)` }}
                        >
                          <div className="flex items-center gap-3">
                            <Calculator className="w-6 h-6" style={{ color: calypso.plum.primary }} />
                            <div>
                              <h3 className="font-bold" style={{ color: calypso.plum.dark }}>
                                Digging Deeper: Math Challenges
                              </h3>
                              <p className="text-sm text-gray-500">Earn seeds by completing math problems!</p>
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 transition-transform ${showMathSection.main ? 'rotate-90' : ''}`} 
                                        style={{ color: calypso.plum.primary }} />
                        </button>
                        
                        <AnimatePresence>
                          {showMathSection.main && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t px-4 pb-4"
                              style={{ borderColor: calypso.plum.light }}
                            >
                              <div className="space-y-4 mt-4">
                                {monthDetails.digging_deeper.map((math, i) => (
                                  <div key={i} className="p-4 rounded-lg bg-gray-50">
                                    <Badge className="mb-2 text-xs" style={{ background: calypso.plum.primary, color: 'white' }}>
                                      {math.lesson}
                                    </Badge>
                                    <p className="text-gray-700 font-medium">{math.prompt}</p>
                                    <p className="text-xs text-gray-500 mt-2">
                                      🌱 +10 seeds for completing this problem
                                    </p>
                                  </div>
                                ))}
                                <div className="text-center p-3 rounded-lg" style={{ background: calypso.mango.glow }}>
                                  <p className="font-semibold" style={{ color: calypso.mango.dark }}>
                                    🌱 Complete all math prompts = +25 bonus seeds!
                                  </p>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    {/* Bonus Challenge */}
                    {(monthDetails.bonus_challenge || monthDetails.wildcard_bonus) && (
                      <div className="p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${calypso.mango.glow}, ${calypso.guava.light}30)` }}>
                        <h3 className="font-bold flex items-center gap-2" style={{ color: calypso.mango.dark }}>
                          <Star className="w-5 h-5" style={{ color: calypso.mango.primary }} />
                          Bonus Challenge
                        </h3>
                        <p className="text-gray-600 mt-1">
                          {monthDetails.bonus_challenge || monthDetails.wildcard_bonus}
                        </p>
                      </div>
                    )}

                    {/* Toolkit */}
                    {(monthDetails.toolkit_physical?.length > 0 || monthDetails.toolkit_digital?.length > 0) && (
                      <div className="grid md:grid-cols-2 gap-4">
                        {monthDetails.toolkit_physical?.length > 0 && (
                          <div className="p-4 rounded-xl bg-gray-50">
                            <h4 className="font-semibold mb-2" style={{ color: calypso.green.dark }}>🧰 Physical Toolkit</h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {monthDetails.toolkit_physical.map((item, i) => (
                                <li key={i}>• {item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {monthDetails.toolkit_digital?.length > 0 && (
                          <div className="p-4 rounded-xl bg-gray-50">
                            <h4 className="font-semibold mb-2" style={{ color: calypso.plum.dark }}>💻 Digital Tools</h4>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {monthDetails.toolkit_digital.map((item, i) => (
                                <li key={i}>• {item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Seed Pack Link */}
                    <Link 
                      to="/marketplace"
                      className="block p-4 rounded-xl transition-all hover:shadow-md"
                      style={{ 
                        background: `linear-gradient(135deg, ${calypso.green.muted}, ${calypso.mango.glow})`,
                        border: `2px solid ${calypso.green.light}`
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                               style={{ background: calypso.green.primary }}>
                            🌱
                          </div>
                          <div>
                            <h4 className="font-bold" style={{ color: calypso.green.dark }}>
                              Get the Seed Pack for this Challenge
                            </h4>
                            <p className="text-sm text-gray-600">
                              $9 with free shipping • USVI teachers get FREE packs
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5" style={{ color: calypso.green.primary }} />
                      </div>
                    </Link>

                    {/* Start Challenge Button */}
                    <div className="pt-4 border-t">
                      {user ? (
                        <Button 
                          className="w-full h-14 text-lg font-semibold text-white rounded-xl"
                          onClick={() => navigate(`/design?challenge=auto-${selectedMonth}`)}
                          style={{ background: `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.mango.dark})` }}
                        >
                          <Sprout className="w-5 h-5 mr-2" />
                          Start This Challenge
                        </Button>
                      ) : (
                        <Button 
                          className="w-full h-14 text-lg font-semibold text-white rounded-xl"
                          onClick={login}
                          style={{ background: `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})` }}
                        >
                          Sign In to Start
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Tropical SVG Background Pattern - MORE VISIBLE
const TropicalBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Large palm leaf - top right */}
    <svg className="absolute -top-10 -right-10 w-[500px] h-[500px]" viewBox="0 0 200 200">
      <path d="M100 180 Q60 140 40 80 Q60 90 100 60 Q140 90 160 80 Q140 140 100 180Z" fill={calypso.green.primary} opacity="0.25"/>
      <path d="M100 60 Q80 40 100 10 Q120 40 100 60" fill={calypso.green.dark} opacity="0.3"/>
      <path d="M90 170 Q50 130 30 70 Q50 80 90 50" fill={calypso.green.light} opacity="0.2"/>
    </svg>
    
    {/* Mango silhouette - bottom left - BRIGHTER */}
    <svg className="absolute -bottom-5 -left-5 w-80 h-80" viewBox="0 0 100 100">
      <ellipse cx="50" cy="55" rx="38" ry="42" fill={calypso.mango.primary} opacity="0.35"/>
      <ellipse cx="45" cy="50" rx="28" ry="32" fill={calypso.mango.light} opacity="0.25"/>
      <path d="M50 15 Q55 2 62 15 Q55 22 50 15" fill={calypso.green.vibrant} opacity="0.5"/>
    </svg>
    
    {/* Coconut palm fronds - top left - MORE VISIBLE */}
    <svg className="absolute top-10 -left-20 w-96 h-96 rotate-[30deg]" viewBox="0 0 100 100">
      <path d="M10 90 Q30 60 50 50 Q40 70 10 90" fill={calypso.green.primary} opacity="0.3"/>
      <path d="M15 85 Q35 55 55 45 Q45 65 15 85" fill={calypso.green.vibrant} opacity="0.25"/>
      <path d="M20 80 Q40 50 60 40 Q50 60 20 80" fill={calypso.green.light} opacity="0.2"/>
    </svg>
    
    {/* Additional palm frond - bottom right */}
    <svg className="absolute -bottom-16 -right-16 w-72 h-72 rotate-[-20deg]" viewBox="0 0 100 100">
      <path d="M10 90 Q30 60 50 50 Q40 70 10 90" fill={calypso.green.dark} opacity="0.2"/>
      <path d="M15 85 Q35 55 55 45 Q45 65 15 85" fill={calypso.green.primary} opacity="0.15"/>
    </svg>
    
    {/* Floating tropical bokeh circles - BRIGHTER */}
    <div className="absolute top-1/4 right-1/4 w-48 h-48 rounded-full blur-3xl" 
         style={{background: `radial-gradient(circle, ${calypso.mango.vibrant}40, transparent)`}}/>
    <div className="absolute bottom-1/3 left-1/3 w-64 h-64 rounded-full blur-3xl"
         style={{background: `radial-gradient(circle, ${calypso.plum.vibrant}25, transparent)`}}/>
    <div className="absolute top-1/2 right-1/3 w-40 h-40 rounded-full blur-2xl"
         style={{background: `radial-gradient(circle, ${calypso.green.vibrant}30, transparent)`}}/>
    <div className="absolute bottom-1/4 right-1/4 w-32 h-32 rounded-full blur-2xl"
         style={{background: `radial-gradient(circle, ${calypso.guava.vibrant}20, transparent)`}}/>
    <div className="absolute bottom-1/3 left-1/3 w-48 h-48 rounded-full blur-3xl"
         style={{background: `radial-gradient(circle, ${calypso.plum.vibrant}25, transparent)`}}/>
  </div>
);

// Rolodex Tab Component - Recipe Card Style - VIBRANT
const RolodexTab = ({ label, isActive, onClick, testId, icon: Icon }) => (
  <motion.button
    onClick={onClick}
    data-testid={testId}
    className={`
      relative px-8 py-4 font-bold text-base transition-all duration-300 cursor-pointer
      ${isActive 
        ? 'text-white z-10' 
        : 'text-gray-600 hover:text-gray-800 hover:bg-white/80'
      }
    `}
    whileHover={{ y: isActive ? 0 : -6, scale: isActive ? 1 : 1.02 }}
    whileTap={{ scale: 0.98 }}
    style={{
      background: isActive 
        ? `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})`
        : 'linear-gradient(180deg, #ffffff, #f0f0f0)',
      borderRadius: '28px 28px 12px 12px',
      boxShadow: isActive 
        ? `0 10px 30px ${calypso.green.primary}50, inset 0 2px 0 rgba(255,255,255,0.3)`
        : '0 4px 15px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)',
      border: isActive ? `2px solid ${calypso.green.vibrant}` : '2px solid rgba(0,0,0,0.08)',
      marginRight: '-14px',
      minWidth: '155px'
    }}
  >
    {/* Curved top edge decoration - BRIGHTER */}
    <div 
      className="absolute -top-1 left-1/2 -translate-x-1/2 w-4/5 h-1.5 rounded-full"
      style={{
        background: isActive 
          ? `linear-gradient(90deg, ${calypso.mango.primary}, ${calypso.mango.vibrant})`
          : 'transparent'
      }}
    />
    
    {/* Tab notch/thumb cut - recipe card style - MORE VISIBLE */}
    <div 
      className="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-10 rounded-l-full"
      style={{
        background: isActive 
          ? `linear-gradient(180deg, ${calypso.mango.vibrant}, ${calypso.mango.primary})`
          : '#e0e0e0',
        boxShadow: isActive ? `inset 2px 0 6px rgba(0,0,0,0.15), 0 0 10px ${calypso.mango.primary}40` : 'none'
      }}
    />
    
    <span className="flex items-center gap-2 relative z-10">
      {Icon && <Icon className="w-5 h-5" />}
      {label}
    </span>
  </motion.button>
);

const ChallengesPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [activeTab, setActiveTab] = useState("active");
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [teacherProfile, setTeacherProfile] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [themes, setThemes] = useState([]);
  const [standards, setStandards] = useState({});
  
  // Class code states
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [classCode, setClassCode] = useState("");
  const [joiningChallenge, setJoiningChallenge] = useState(false);
  
  // Certificate states
  const [certificateProgress, setCertificateProgress] = useState(null);
  const [viewingCertificate, setViewingCertificate] = useState(null);
  const [generatingCertificate, setGeneratingCertificate] = useState(false);

  useEffect(() => {
    loadChallenges();
    loadThemes();
    loadStandards();
    if (user) {
      loadTeacherProfile();
      loadCertificateProgress();
    }
  }, [user, activeTab]);

  const loadChallenges = async () => {
    setLoading(true);
    try {
      const endpoint = activeTab === "active" ? "/challenges/active" : "/challenges";
      const response = await axios.get(`${API}${endpoint}`);
      setChallenges(response.data.challenges || []);
    } catch (error) {
      console.error("Failed to load challenges:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadThemes = async () => {
    try {
      const response = await axios.get(`${API}/challenges/themes`);
      setThemes(response.data.themes || []);
    } catch (error) {
      console.error("Failed to load themes:", error);
    }
  };

  const loadStandards = async () => {
    try {
      const response = await axios.get(`${API}/educational-standards`);
      setStandards(response.data.standards || {});
    } catch (error) {
      console.error("Failed to load standards:", error);
    }
  };

  const loadTeacherProfile = async () => {
    try {
      const response = await axios.get(`${API}/teachers/profile`, { withCredentials: true });
      setTeacherProfile(response.data);
    } catch (error) {
      console.error("Failed to load teacher profile:", error);
    }
  };

  const loadCertificateProgress = async () => {
    try {
      const response = await axios.get(`${API}/certificates/my-progress`, { withCredentials: true });
      setCertificateProgress(response.data);
    } catch (error) {
      console.error("Failed to load certificate progress:", error);
    }
  };

  const joinChallengeByCode = async () => {
    if (!user) {
      toast.error("Please log in to join a challenge");
      login();
      return;
    }

    if (!classCode || classCode.length !== 6) {
      toast.error("Please enter a valid 6-character class code");
      return;
    }

    setJoiningChallenge(true);
    try {
      const response = await axios.post(
        `${API}/challenges/join`,
        { class_code: classCode },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowJoinModal(false);
      setClassCode("");
      loadChallenges();
      if (response.data.challenge) {
        loadChallengeDetails(response.data.challenge.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join challenge");
    } finally {
      setJoiningChallenge(false);
    }
  };

  const generateCertificate = async (challengeId) => {
    if (!user) {
      toast.error("Please log in");
      login();
      return;
    }

    setGeneratingCertificate(true);
    try {
      const response = await axios.post(
        `${API}/certificates/generate/${challengeId}`,
        {},
        { withCredentials: true }
      );
      setViewingCertificate(response.data.certificate);
      toast.success(response.data.message);
      loadCertificateProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate certificate");
    } finally {
      setGeneratingCertificate(false);
    }
  };

  const loadChallengeDetails = async (challengeId) => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeId}`);
      setSelectedChallenge(response.data);
    } catch (error) {
      toast.error("Failed to load challenge details");
    }
  };

  const voteForSubmission = async (submissionId) => {
    if (!user) {
      toast.error("Please log in to vote");
      login();
      return;
    }

    try {
      await axios.post(
        `${API}/challenges/${selectedChallenge.id}/vote/${submissionId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Vote recorded!");
      loadChallengeDetails(selectedChallenge.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to vote");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff < 0) return "Ended";
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  return (
    <div 
      className="min-h-screen relative"
      style={{
        background: `linear-gradient(160deg, ${calypso.coconut.cream} 0%, ${calypso.green.muted} 25%, ${calypso.mango.glow} 50%, ${calypso.green.muted} 75%, ${calypso.plum.light}15 100%)`
      }}
    >
      {/* Tropical Background Elements */}
      <TropicalBackground />
      
      {/* Certificate View Modal */}
      <AnimatePresence>
        {viewingCertificate && (
          <CertificateView 
            certificate={viewingCertificate} 
            onClose={() => setViewingCertificate(null)} 
          />
        )}
      </AnimatePresence>

      {/* Join by Class Code Modal - Tropical Styled */}
      <AnimatePresence>
        {showJoinModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowJoinModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-3xl max-w-md w-full p-8 relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              style={{
                boxShadow: `0 25px 50px -12px ${calypso.green.primary}30`
              }}
            >
              {/* Decorative top bar */}
              <div 
                className="absolute top-0 left-0 right-0 h-2"
                style={{
                  background: `linear-gradient(90deg, ${calypso.green.primary}, ${calypso.mango.primary}, ${calypso.plum.primary})`
                }}
              />
              
              <div className="text-center mb-6">
                <div 
                  className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${calypso.green.light}30, ${calypso.mango.light}30)`,
                    border: `3px solid ${calypso.green.primary}`
                  }}
                >
                  <Key className="w-10 h-10" style={{ color: calypso.green.primary }} />
                </div>
                <h2 className="text-2xl font-bold" style={{ color: calypso.green.dark }}>
                  Join with Class Code
                </h2>
                <p className="text-gray-500 text-sm mt-2">
                  Enter the 6-character code your teacher gave you
                </p>
              </div>
              
              <div className="space-y-4">
                <Input
                  value={classCode}
                  onChange={(e) => setClassCode(e.target.value.toUpperCase().slice(0, 6))}
                  placeholder="ABC123"
                  className="text-center text-2xl tracking-[0.3em] font-mono h-14 border-2 rounded-xl"
                  maxLength={6}
                  data-testid="class-code-input"
                  style={{ 
                    borderColor: classCode.length === 6 ? calypso.green.primary : '#e5e7eb',
                    background: classCode.length === 6 ? calypso.green.muted : 'white'
                  }}
                />
                
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowJoinModal(false)} 
                    className="flex-1 h-12 rounded-xl border-2"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={joinChallengeByCode} 
                    disabled={joiningChallenge || classCode.length !== 6}
                    className="flex-1 h-12 rounded-xl text-white font-semibold"
                    data-testid="join-challenge-btn"
                    style={{
                      background: classCode.length === 6 
                        ? `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})`
                        : '#9ca3af'
                    }}
                  >
                    {joiningChallenge ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                    Join Challenge
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header - Tropical Style */}
      <header 
        className="sticky top-0 z-40 border-b"
        style={{
          background: `linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))`,
          backdropFilter: 'blur(12px)',
          borderColor: `${calypso.green.primary}20`
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/")} 
              data-testid="back-btn"
              className="hover:bg-green-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 
                className="text-2xl font-bold flex items-center gap-2"
                style={{ color: calypso.green.dark }}
              >
                <span className="text-3xl">🌱</span>
                GROWTH
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowJoinModal(true)} 
              data-testid="join-with-code-btn"
              className="rounded-xl border-2 hover:border-green-400 hover:bg-green-50"
              style={{ borderColor: calypso.green.light }}
            >
              <Key className="w-4 h-4 mr-2" style={{ color: calypso.green.primary }} /> Join with Code
            </Button>
            {teacherProfile?.is_teacher && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/teacher")}
                  className="rounded-xl border-2"
                  style={{ borderColor: calypso.plum.light }}
                >
                  <GraduationCap className="w-4 h-4 mr-2" style={{ color: calypso.plum.primary }} /> Dashboard
                </Button>
                <Button 
                  onClick={() => setShowCreateModal(true)} 
                  data-testid="create-challenge-btn"
                  className="rounded-xl text-white font-semibold"
                  style={{
                    background: `linear-gradient(135deg, ${calypso.mango.primary}, ${calypso.mango.dark})`
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Challenge
                </Button>
              </>
            )}
            {!teacherProfile?.is_teacher && user && (
              <Button 
                variant="outline" 
                onClick={() => navigate("/challenges/teacher-register")}
                className="rounded-xl border-2"
                style={{ borderColor: calypso.plum.light }}
              >
                <GraduationCap className="w-4 h-4 mr-2" /> Become a Teacher
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 relative z-10">
        {/* Hero Section - Challenge your Growth - MORE VIBRANT */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <h2 
            className="text-5xl md:text-6xl font-extrabold mb-4 drop-shadow-sm"
            style={{
              background: `linear-gradient(135deg, ${calypso.green.dark} 0%, ${calypso.green.primary} 30%, ${calypso.mango.primary} 60%, ${calypso.guava.primary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 4px 30px rgba(0,166,107,0.3)'
            }}
          >
            Challenge your Growth.
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Compete in garden design challenges, earn certificates, and grow your skills alongside fellow gardeners
          </p>
        </motion.div>

        {/* Certificate Progress Card */}
        {user && certificateProgress && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <CertificateProgressCard 
              progress={certificateProgress}
              onViewCertificates={() => {
                if (certificateProgress.certificates_earned?.length > 0) {
                  setViewingCertificate(certificateProgress.certificates_earned[0]);
                }
              }}
            />
          </motion.div>
        )}
        {/* Challenge Detail Modal - Tropical Style */}
        <AnimatePresence>
          {selectedChallenge && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
              onClick={() => setSelectedChallenge(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 30 }}
                className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
                onClick={(e) => e.stopPropagation()}
                style={{
                  boxShadow: `0 30px 60px ${calypso.green.primary}25`
                }}
              >
                {/* Challenge Header - Tropical Gradient */}
                <div 
                  className="p-8 text-white rounded-t-3xl relative overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, ${calypso.green.dark}, ${calypso.green.primary}, ${calypso.mango.dark})`
                  }}
                >
                  {/* Decorative circles */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-20"
                       style={{ background: calypso.mango.light }} />
                  <div className="absolute -bottom-5 -left-5 w-32 h-32 rounded-full opacity-10"
                       style={{ background: 'white' }} />
                  
                  <div className="flex justify-between items-start relative z-10">
                    <div>
                      <Badge 
                        className="mb-3 rounded-full px-4 py-1"
                        style={{ 
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          backdropFilter: 'blur(8px)'
                        }}
                      >
                        {selectedChallenge.theme}
                      </Badge>
                      <h2 className="text-3xl font-bold mb-2">{selectedChallenge.title}</h2>
                      <p className="text-white/80">by {selectedChallenge.teacher_name}</p>
                      {selectedChallenge.school_name && (
                        <p className="text-white/60 text-sm mt-1">{selectedChallenge.school_name}</p>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSelectedChallenge(null)} 
                      className="text-white hover:bg-white/20 rounded-full w-10 h-10 p-0"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-6 mt-6 text-sm relative z-10">
                    <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <Clock className="w-4 h-4" />
                      {getTimeRemaining(selectedChallenge.end_date)}
                    </span>
                    <span className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                      <Users className="w-4 h-4" />
                      {selectedChallenge.submission_count || 0} submissions
                    </span>
                  </div>
                </div>

                <div className="p-8 space-y-8">
                  {/* Description & Prompt */}
                  <div>
                    <h3 
                      className="font-bold text-lg mb-3 flex items-center gap-2"
                      style={{ color: calypso.green.dark }}
                    >
                      <Sparkles className="w-5 h-5" style={{ color: calypso.mango.primary }} />
                      Challenge Prompt
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {selectedChallenge.prompt || selectedChallenge.description}
                    </p>
                  </div>

                  {/* Requirements - Tropical Card */}
                  {(selectedChallenge.yield_goal_lbs || selectedChallenge.calorie_goal || selectedChallenge.water_limit_gallons) && (
                    <div 
                      className="rounded-2xl p-6"
                      style={{
                        background: `linear-gradient(135deg, ${calypso.mango.glow}, ${calypso.green.muted})`
                      }}
                    >
                      <h3 
                        className="font-bold text-lg mb-4 flex items-center gap-2"
                        style={{ color: calypso.green.dark }}
                      >
                        <Target className="w-5 h-5" style={{ color: calypso.mango.primary }} />
                        Goals & Requirements
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {selectedChallenge.yield_goal_lbs && (
                          <div 
                            className="text-center p-4 rounded-xl bg-white/70"
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                          >
                            <Scale className="w-7 h-7 mx-auto mb-2" style={{ color: calypso.green.primary }} />
                            <p className="text-2xl font-bold" style={{ color: calypso.green.dark }}>
                              {selectedChallenge.yield_goal_lbs}
                            </p>
                            <p className="text-xs text-gray-500">lbs yield goal</p>
                          </div>
                        )}
                        {selectedChallenge.calorie_goal && (
                          <div 
                            className="text-center p-4 rounded-xl bg-white/70"
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                          >
                            <Flame className="w-7 h-7 mx-auto mb-2" style={{ color: calypso.mango.primary }} />
                            <p className="text-2xl font-bold" style={{ color: calypso.mango.dark }}>
                              {selectedChallenge.calorie_goal.toLocaleString()}
                            </p>
                            <p className="text-xs text-gray-500">calorie goal</p>
                          </div>
                        )}
                        {selectedChallenge.water_limit_gallons && (
                          <div 
                            className="text-center p-4 rounded-xl bg-white/70"
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                          >
                            <Droplets className="w-7 h-7 mx-auto mb-2" style={{ color: '#3B82F6' }} />
                            <p className="text-2xl font-bold text-blue-700">
                              {selectedChallenge.water_limit_gallons}
                            </p>
                            <p className="text-xs text-gray-500">gallon limit</p>
                          </div>
                        )}
                        {selectedChallenge.min_plants && (
                          <div 
                            className="text-center p-4 rounded-xl bg-white/70"
                            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                          >
                            <Leaf className="w-7 h-7 mx-auto mb-2" style={{ color: calypso.green.primary }} />
                            <p className="text-2xl font-bold" style={{ color: calypso.green.dark }}>
                              {selectedChallenge.min_plants}-{selectedChallenge.max_plants}
                            </p>
                            <p className="text-xs text-gray-500">plants required</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Educational Standards - Tropical Plum */}
                  {selectedChallenge.educational_standards?.length > 0 && (
                    <div 
                      className="rounded-2xl p-6"
                      style={{
                        background: `linear-gradient(135deg, ${calypso.plum.light}20, ${calypso.plum.light}10)`
                      }}
                    >
                      <h3 
                        className="font-bold text-lg mb-4 flex items-center gap-2"
                        style={{ color: calypso.plum.dark }}
                      >
                        <BookOpen className="w-5 h-5" style={{ color: calypso.plum.primary }} />
                        Educational Standards
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedChallenge.educational_standards.map((std, i) => (
                          <Badge 
                            key={i} 
                            className="rounded-full px-3 py-1"
                            style={{
                              background: 'white',
                              color: calypso.plum.dark,
                              border: `1px solid ${calypso.plum.light}`
                            }}
                          >
                            {std}
                          </Badge>
                        ))}
                      </div>
                      {selectedChallenge.learning_objectives?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-semibold mb-2" style={{ color: calypso.plum.dark }}>
                            Learning Objectives:
                          </p>
                          <ul className="text-sm list-disc list-inside space-y-1" style={{ color: calypso.plum.primary }}>
                            {selectedChallenge.learning_objectives.map((obj, i) => (
                              <li key={i}>{obj}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Submissions - Tropical Style */}
                  <div>
                    <h3 
                      className="font-bold text-lg mb-5 flex items-center gap-2"
                      style={{ color: calypso.green.dark }}
                    >
                      <Users className="w-5 h-5" style={{ color: calypso.green.primary }} />
                      Submissions ({selectedChallenge.submissions?.length || 0})
                    </h3>
                    
                    {selectedChallenge.submissions?.length > 0 ? (
                      <div className="space-y-4">
                        {selectedChallenge.submissions.map((sub, index) => (
                          <Card 
                            key={sub.id} 
                            className="border-0 overflow-hidden"
                            style={{
                              background: index < 3 
                                ? `linear-gradient(135deg, ${calypso.mango.glow}, white)`
                                : 'white',
                              boxShadow: index < 3 
                                ? `0 4px 20px ${calypso.mango.primary}20`
                                : '0 2px 10px rgba(0,0,0,0.05)',
                              borderRadius: '16px'
                            }}
                          >
                            <CardContent className="p-5">
                              <div className="flex items-start gap-4">
                                {/* Rank Badge - Tropical Colors */}
                                <div 
                                  className="w-14 h-14 rounded-2xl flex items-center justify-center font-bold text-lg"
                                  style={{
                                    background: index === 0 
                                      ? `linear-gradient(135deg, ${calypso.mango.primary}, ${calypso.mango.light})`
                                      : index === 1 
                                        ? 'linear-gradient(135deg, #C0C0C0, #E8E8E8)'
                                        : index === 2 
                                          ? `linear-gradient(135deg, ${calypso.coconut.shell}, #A0887E)`
                                          : `linear-gradient(135deg, ${calypso.green.muted}, white)`,
                                    color: index < 3 ? 'white' : calypso.green.dark,
                                    boxShadow: index < 3 ? '0 4px 12px rgba(0,0,0,0.15)' : 'none'
                                  }}
                                >
                                  {sub.rank || index + 1}
                                </div>
                                
                                {/* Design Preview */}
                                <div 
                                  className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden"
                                  style={{
                                    background: `linear-gradient(135deg, ${calypso.green.muted}, ${calypso.mango.glow})`,
                                    border: `2px solid ${calypso.green.light}40`
                                  }}
                                >
                                  {sub.design_snapshot?.background_image ? (
                                    <img src={sub.design_snapshot.background_image} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="text-2xl">
                                      {sub.design_snapshot?.plants?.slice(0, 3).map((p, i) => (
                                        <span key={i}>{p.icon}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Info */}
                                <div className="flex-1">
                                  <h4 className="font-bold" style={{ color: calypso.green.dark }}>
                                    {sub.design_name}
                                  </h4>
                                  <p className="text-sm text-gray-500">by {sub.user_name}</p>
                                  
                                  {/* Yield Stats - Tropical Pills */}
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <span 
                                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                      style={{ 
                                        background: `${calypso.green.light}30`,
                                        color: calypso.green.dark
                                      }}
                                    >
                                      <Scale className="w-3 h-3" />
                                      {sub.yield_stats?.total_yield_lbs} lbs
                                    </span>
                                    <span 
                                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                      style={{ 
                                        background: `${calypso.mango.light}30`,
                                        color: calypso.mango.dark
                                      }}
                                    >
                                      <Flame className="w-3 h-3" />
                                      {sub.yield_stats?.total_calories?.toLocaleString()} cal
                                    </span>
                                    <span 
                                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                                      style={{ 
                                        background: `${calypso.green.primary}20`,
                                        color: calypso.green.dark
                                      }}
                                    >
                                      <DollarSign className="w-3 h-3" />
                                      ${sub.yield_stats?.total_market_value}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Vote Button - Tropical */}
                                <div className="text-center">
                                  <Button
                                    variant={sub.voters?.includes(user?.user_id) ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => voteForSubmission(sub.id)}
                                    disabled={sub.user_id === user?.user_id}
                                    className="mb-2 rounded-xl"
                                    style={{
                                      background: sub.voters?.includes(user?.user_id) 
                                        ? `linear-gradient(135deg, ${calypso.guava.primary}, ${calypso.guava.light})`
                                        : 'white',
                                      borderColor: calypso.guava.light,
                                      color: sub.voters?.includes(user?.user_id) ? 'white' : calypso.guava.primary
                                    }}
                                  >
                                    <Heart className={`w-4 h-4 mr-1 ${sub.voters?.includes(user?.user_id) ? 'fill-current' : ''}`} />
                                    Vote
                                  </Button>
                                  <p className="text-xl font-bold" style={{ color: calypso.guava.primary }}>
                                    {sub.vote_count}
                                  </p>
                                  <p className="text-xs text-gray-400">votes</p>
                                </div>
                              </div>
                              
                              {/* Teacher Feedback - Plum Theme */}
                              {sub.teacher_feedback && (
                                <div 
                                  className="mt-4 p-4 rounded-xl"
                                  style={{ 
                                    background: `linear-gradient(135deg, ${calypso.plum.light}15, ${calypso.plum.light}10)`,
                                    border: `1px solid ${calypso.plum.light}40`
                                  }}
                                >
                                  <p className="text-sm font-semibold" style={{ color: calypso.plum.dark }}>
                                    Teacher Feedback:
                                  </p>
                                  <p className="text-sm mt-1" style={{ color: calypso.plum.primary }}>
                                    {sub.teacher_feedback}
                                  </p>
                                  {sub.teacher_score && (
                                    <Badge 
                                      className="mt-2"
                                      style={{ 
                                        background: calypso.plum.primary,
                                        color: 'white'
                                      }}
                                    >
                                      Score: {sub.teacher_score}/100
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div 
                          className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${calypso.green.muted}, ${calypso.mango.glow})` }}
                        >
                          <Leaf className="w-10 h-10" style={{ color: calypso.green.light }} />
                        </div>
                        <p className="text-gray-500 mb-4">No submissions yet. Be the first!</p>
                        <Button 
                          onClick={() => navigate("/design")}
                          className="rounded-xl text-white"
                          style={{
                            background: `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})`
                          }}
                        >
                          Create Your Design
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Submit Button - Tropical */}
                  {user && selectedChallenge.status === "active" && (
                    <div className="border-t pt-6" style={{ borderColor: `${calypso.green.light}30` }}>
                      <Button 
                        className="w-full rounded-xl h-14 text-lg font-semibold text-white"
                        onClick={() => navigate(`/design?challenge=${selectedChallenge.id}`)}
                        style={{
                          background: `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.mango.dark})`
                        }}
                      >
                        <Sparkles className="w-5 h-5 mr-2" />
                        Submit Your Design
                      </Button>
                    </div>
                  )}
                  
                  {/* Certificate Button - Tropical Green */}
                  {user && selectedChallenge.submissions?.find(s => s.user_id === user.user_id) && (
                    <div className="border-t pt-6" style={{ borderColor: `${calypso.green.light}30` }}>
                      <Button 
                        variant="outline" 
                        className="w-full rounded-xl h-14 text-lg font-semibold border-2"
                        onClick={() => generateCertificate(selectedChallenge.id)}
                        disabled={generatingCertificate}
                        data-testid="get-certificate-btn"
                        style={{
                          borderColor: calypso.green.primary,
                          color: calypso.green.dark,
                          background: `linear-gradient(135deg, ${calypso.green.muted}, white)`
                        }}
                      >
                        {generatingCertificate ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Award className="w-5 h-5 mr-2" style={{ color: calypso.green.primary }} />
                        )}
                        Get Your Certificate
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create Challenge Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <CreateChallengeModal
              onClose={() => setShowCreateModal(false)}
              onCreated={() => {
                setShowCreateModal(false);
                loadChallenges();
              }}
              themes={themes}
              standards={standards}
            />
          )}
        </AnimatePresence>

        {/* Rolodex Style Tabs */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center mb-10"
        >
          <div 
            className="inline-flex items-end p-2 rounded-3xl"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.8), rgba(255,255,255,0.6))',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1)'
            }}
          >
            <RolodexTab 
              label="Active"
              icon={Sparkles}
              isActive={activeTab === "active"}
              onClick={() => setActiveTab("active")}
              testId="tab-active"
            />
            <RolodexTab 
              label="12-Month"
              icon={Calendar}
              isActive={activeTab === "monthly"}
              onClick={() => setActiveTab("monthly")}
              testId="tab-monthly"
            />
            <RolodexTab 
              label="All Challenges"
              icon={Trophy}
              isActive={activeTab === "all"}
              onClick={() => setActiveTab("all")}
              testId="tab-all"
            />
            <RolodexTab 
              label="My Challenges"
              icon={Leaf}
              isActive={activeTab === "my"}
              onClick={() => setActiveTab("my")}
              testId="tab-my"
            />
          </div>
        </motion.div>

        {/* 12-Month Auto Challenge Calendar */}
        {activeTab === "monthly" && (
          <MonthlyChallengCalendar user={user} login={login} />
        )}

        {/* Challenges Grid - Tropical Cards */}
        {activeTab !== "monthly" && loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: `linear-gradient(135deg, ${calypso.green.light}40, ${calypso.mango.light}40)` }}
            >
              <Loader2 
                className="w-8 h-8 animate-spin" 
                style={{ color: calypso.green.primary }}
              />
            </div>
            <p className="text-gray-500">Loading challenges...</p>
          </div>
        ) : activeTab !== "monthly" && challenges.length > 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {challenges.map((challenge, index) => (
              <motion.div
                key={challenge.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="cursor-pointer"
                onClick={() => loadChallengeDetails(challenge.id)}
                data-testid={`challenge-${challenge.id}`}
              >
                <Card 
                  className="h-full overflow-hidden border-0"
                  style={{
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
                    boxShadow: `0 10px 40px ${calypso.green.primary}15, 0 2px 10px rgba(0,0,0,0.05)`,
                    borderRadius: '24px'
                  }}
                >
                  {/* Tropical top accent */}
                  <div 
                    className="h-3"
                    style={{
                      background: challenge.status === 'active' 
                        ? `linear-gradient(90deg, ${calypso.green.primary}, ${calypso.green.light}, ${calypso.mango.primary})`
                        : challenge.status === 'completed' 
                          ? `linear-gradient(90deg, ${calypso.coconut.shell}, #a0a0a0)`
                          : `linear-gradient(90deg, ${calypso.mango.primary}, ${calypso.plum.light})`
                    }}
                  />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge 
                        className="rounded-full px-3 py-1 text-sm font-medium"
                        style={{
                          background: `${calypso.plum.light}30`,
                          color: calypso.plum.dark,
                          border: `1px solid ${calypso.plum.light}`
                        }}
                      >
                        {challenge.theme}
                      </Badge>
                      <Badge 
                        className="rounded-full px-3 py-1 text-sm font-semibold text-white"
                        style={{
                          background: challenge.status === 'active' 
                            ? calypso.green.primary 
                            : '#9ca3af'
                        }}
                      >
                        {challenge.status}
                      </Badge>
                    </div>
                    
                    <h3 
                      className="font-bold text-xl mb-2"
                      style={{ color: calypso.green.dark }}
                    >
                      {challenge.title}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-2 mb-5">
                      {challenge.description || challenge.prompt}
                    </p>
                    
                    {/* Goals Preview - Tropical Pill Style */}
                    <div className="flex items-center gap-2 mb-5 flex-wrap">
                      {challenge.yield_goal_lbs && (
                        <span 
                          className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                          style={{ 
                            background: `${calypso.green.light}30`,
                            color: calypso.green.dark
                          }}
                        >
                          <Scale className="w-3.5 h-3.5" /> {challenge.yield_goal_lbs} lbs
                        </span>
                      )}
                      {challenge.min_plants && (
                        <span 
                          className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium"
                          style={{ 
                            background: `${calypso.mango.light}30`,
                            color: calypso.mango.dark
                          }}
                        >
                          <Leaf className="w-3.5 h-3.5" /> {challenge.min_plants}+ plants
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" style={{ color: calypso.plum.primary }} />
                        {challenge.submission_count} submissions
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" style={{ color: calypso.mango.primary }} />
                        {getTimeRemaining(challenge.end_date)}
                      </span>
                    </div>
                    
                    <div 
                      className="mt-4 pt-4 flex items-center justify-between"
                      style={{ borderTop: `1px solid ${calypso.green.light}30` }}
                    >
                      <div>
                        <p className="text-xs text-gray-400">Created by</p>
                        <p className="text-sm font-semibold" style={{ color: calypso.green.dark }}>
                          {challenge.teacher_name}
                        </p>
                      </div>
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ 
                          background: `linear-gradient(135deg, ${calypso.green.light}40, ${calypso.mango.light}40)` 
                        }}
                      >
                        <ChevronRight className="w-5 h-5" style={{ color: calypso.green.primary }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        ) : activeTab !== "monthly" ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div 
              className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${calypso.green.muted}, ${calypso.mango.glow})` 
              }}
            >
              <Trophy className="w-12 h-12" style={{ color: calypso.mango.primary }} />
            </div>
            <h3 
              className="text-2xl font-bold mb-3"
              style={{ color: calypso.green.dark }}
            >
              No Challenges Yet
            </h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              {teacherProfile?.is_teacher 
                ? "Create your first challenge to engage students and inspire garden designs!"
                : "Check back soon for exciting new design challenges!"}
            </p>
            {teacherProfile?.is_teacher && (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="rounded-xl px-8 py-6 text-lg font-semibold text-white"
                style={{
                  background: `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})`
                }}
              >
                <Plus className="w-5 h-5 mr-2" /> Create Your First Challenge
              </Button>
            )}
          </motion.div>
        ) : null}
      </main>
    </div>
  );
};

// Create Challenge Modal Component - Tropical Style
const CreateChallengeModal = ({ onClose, onCreated, themes, standards }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    theme: "food_security",
    prompt: "",
    local_theme: "",
    grade_level: "6-8",
    min_plants: 5,
    max_plants: 30,
    yield_goal_lbs: "",
    calorie_goal: "",
    water_limit_gallons: "",
    educational_standards: [],
    learning_objectives: [""],
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!formData.title || !formData.prompt) {
      toast.error("Please fill in the title and prompt");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...formData,
        yield_goal_lbs: formData.yield_goal_lbs ? parseFloat(formData.yield_goal_lbs) : null,
        calorie_goal: formData.calorie_goal ? parseInt(formData.calorie_goal) : null,
        water_limit_gallons: formData.water_limit_gallons ? parseFloat(formData.water_limit_gallons) : null,
        learning_objectives: formData.learning_objectives.filter(o => o.trim()),
        end_date: new Date(formData.end_date).toISOString()
      };

      await axios.post(`${API}/challenges`, payload, { withCredentials: true });
      toast.success("Challenge created!");
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create challenge");
    } finally {
      setCreating(false);
    }
  };

  const addLearningObjective = () => {
    setFormData(prev => ({
      ...prev,
      learning_objectives: [...prev.learning_objectives, ""]
    }));
  };

  const updateLearningObjective = (index, value) => {
    const updated = [...formData.learning_objectives];
    updated[index] = value;
    setFormData(prev => ({ ...prev, learning_objectives: updated }));
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 30 }}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8"
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: `0 30px 60px ${calypso.green.primary}25`
        }}
      >
        {/* Header with tropical gradient */}
        <div 
          className="p-6 rounded-t-3xl flex justify-between items-center"
          style={{
            background: `linear-gradient(135deg, ${calypso.mango.glow}, ${calypso.green.muted})`
          }}
        >
          <h2 
            className="text-2xl font-bold flex items-center gap-3"
            style={{ color: calypso.green.dark }}
          >
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${calypso.mango.primary}, ${calypso.mango.dark})` }}
            >
              <Trophy className="w-5 h-5 text-white" />
            </div>
            Create Challenge
          </h2>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClose}
            className="rounded-full w-10 h-10 p-0 hover:bg-white/50"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold" style={{ color: calypso.green.dark }}>
                Challenge Title *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Spring Food Security Challenge"
                className="mt-1.5 rounded-xl border-2 focus:border-green-400"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: calypso.green.dark }}>
                Theme
              </Label>
              <select
                value={formData.theme}
                onChange={(e) => setFormData(prev => ({ ...prev, theme: e.target.value }))}
                className="w-full p-3 mt-1.5 border-2 rounded-xl focus:border-green-400 focus:outline-none"
              >
                {themes.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: calypso.green.dark }}>
                Local Theme (optional)
              </Label>
              <Input
                value={formData.local_theme}
                onChange={(e) => setFormData(prev => ({ ...prev, local_theme: e.target.value }))}
                placeholder="e.g., Desert Southwest, Pacific Northwest"
                className="mt-1.5 rounded-xl border-2 focus:border-green-400"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold" style={{ color: calypso.green.dark }}>
                Challenge Prompt *
              </Label>
              <textarea
                value={formData.prompt}
                onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                placeholder="Describe the challenge. What should students create?"
                className="w-full p-3 mt-1.5 border-2 rounded-xl h-24 focus:border-green-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Goals - Tropical Mango Theme */}
          <div 
            className="rounded-2xl p-5"
            style={{
              background: `linear-gradient(135deg, ${calypso.mango.glow}, ${calypso.mango.light}20)`
            }}
          >
            <h3 
              className="font-bold mb-4 flex items-center gap-2"
              style={{ color: calypso.mango.dark }}
            >
              <Target className="w-5 h-5" style={{ color: calypso.mango.primary }} />
              Goals & Requirements
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Yield Goal (lbs)</Label>
                <Input
                  type="number"
                  value={formData.yield_goal_lbs}
                  onChange={(e) => setFormData(prev => ({ ...prev, yield_goal_lbs: e.target.value }))}
                  placeholder="e.g., 50"
                  className="mt-1 rounded-xl border-2 bg-white"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Calorie Goal</Label>
                <Input
                  type="number"
                  value={formData.calorie_goal}
                  onChange={(e) => setFormData(prev => ({ ...prev, calorie_goal: e.target.value }))}
                  placeholder="e.g., 10000"
                  className="mt-1 rounded-xl border-2 bg-white"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Water Limit (gallons)</Label>
                <Input
                  type="number"
                  value={formData.water_limit_gallons}
                  onChange={(e) => setFormData(prev => ({ ...prev, water_limit_gallons: e.target.value }))}
                  placeholder="e.g., 500"
                  className="mt-1 rounded-xl border-2 bg-white"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-600">Plant Count Range</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    value={formData.min_plants}
                    onChange={(e) => setFormData(prev => ({ ...prev, min_plants: parseInt(e.target.value) }))}
                    placeholder="Min"
                    className="rounded-xl border-2 bg-white"
                  />
                  <Input
                    type="number"
                    value={formData.max_plants}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_plants: parseInt(e.target.value) }))}
                    placeholder="Max"
                    className="rounded-xl border-2 bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Educational Standards - Plum Theme */}
          <div 
            className="rounded-2xl p-5"
            style={{
              background: `linear-gradient(135deg, ${calypso.plum.light}15, ${calypso.plum.light}08)`
            }}
          >
            <h3 
              className="font-bold mb-4 flex items-center gap-2"
              style={{ color: calypso.plum.dark }}
            >
              <BookOpen className="w-5 h-5" style={{ color: calypso.plum.primary }} />
              Educational Standards
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-600">Grade Level</Label>
                <select
                  value={formData.grade_level}
                  onChange={(e) => setFormData(prev => ({ ...prev, grade_level: e.target.value }))}
                  className="w-full p-3 mt-1 border-2 rounded-xl bg-white focus:border-purple-400 focus:outline-none"
                >
                  <option value="K-2">K-2 (Early Elementary)</option>
                  <option value="3-5">3-5 (Upper Elementary)</option>
                  <option value="6-8">6-8 (Middle School)</option>
                  <option value="9-12">9-12 (High School)</option>
                  <option value="K-12">K-12 (All Grades)</option>
                </select>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-600">Standards (select applicable)</Label>
                <div className="max-h-40 overflow-y-auto border-2 rounded-xl bg-white p-3 mt-1 space-y-1">
                  {Object.entries(standards).map(([key, std]) => (
                    <div key={key}>
                      <p className="text-xs font-bold text-gray-400 mt-2 uppercase tracking-wider">
                        {std.name}
                      </p>
                      {std.standards?.map(s => (
                        <label 
                          key={s.code} 
                          className="flex items-center gap-2 text-sm hover:bg-purple-50 p-1.5 rounded-lg cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.educational_standards.includes(s.code)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  educational_standards: [...prev.educational_standards, s.code]
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  educational_standards: prev.educational_standards.filter(c => c !== s.code)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <span 
                            className="font-mono text-xs px-1.5 py-0.5 rounded"
                            style={{ 
                              background: `${calypso.plum.light}30`,
                              color: calypso.plum.dark
                            }}
                          >
                            {s.code}
                          </span>
                          <span className="truncate text-gray-600">{s.description}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-gray-600">Learning Objectives</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={addLearningObjective}
                    className="text-sm"
                    style={{ color: calypso.plum.primary }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                {formData.learning_objectives.map((obj, i) => (
                  <Input
                    key={i}
                    value={obj}
                    onChange={(e) => updateLearningObjective(i, e.target.value)}
                    placeholder={`Objective ${i + 1}`}
                    className="mb-2 rounded-xl border-2 bg-white"
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <Label className="text-sm font-semibold" style={{ color: calypso.green.dark }}>
              Challenge End Date
            </Label>
            <Input
              type="date"
              value={formData.end_date}
              onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
              min={new Date().toISOString().split('T')[0]}
              className="mt-1.5 rounded-xl border-2"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t" style={{ borderColor: `${calypso.green.light}30` }}>
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="flex-1 h-12 rounded-xl border-2"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={creating} 
              className="flex-1 h-12 rounded-xl text-white font-semibold"
              style={{
                background: `linear-gradient(135deg, ${calypso.green.primary}, ${calypso.green.dark})`
              }}
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trophy className="w-4 h-4 mr-2" />}
              Create Challenge
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ChallengesPage;
